import { useEffect, useMemo, useRef, useState } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { Check, Pencil, Route as RouteIcon, Trash2, X } from 'lucide-react'
import { useAuth } from '@/lib/auth/useAuth'
import {
  deleteSavedRoute,
  fetchSavedRoutes,
  onSavedRoutesChanged,
  reorderSavedRoutes,
  renameSavedRoute,
  routeStatsFresh,
  saveRouteStats,
  type SavedRouteRow,
} from '@/lib/auth/auth'
import { supabase } from '@/integrations/supabase/client'
import { useRoadStore } from '@/lib/roads/store'
import { useAuthUi } from '@/lib/auth/authUi'
import { formatDuration, roadDurationSeconds } from '@/lib/roads/api'
import { buildRouteWaypoints } from '@/lib/roads/ride'
import type { RoadRecord } from '@/lib/roads/types'

// Shared, account-independent totals: shown straight from the route_stats row
// when present; otherwise a per-road fallback sum (never a fresh routing
// call — recalculation happens only when the route is loaded/selected).
function SavedRouteStats({ row, roads }: { row: SavedRouteRow; roads: RoadRecord[] }) {
  const miles = useMemo(() => {
    if (row.stats_miles != null) return Math.round(row.stats_miles)
    const sum = row.road_ids.reduce(
      (s, id) => s + (roads.find((r) => r.id === id)?.distance_miles ?? 0),
      0,
    )
    return Math.round(sum)
  }, [row.stats_miles, row.road_ids, roads])
  const seconds = useMemo(() => {
    if (row.stats_seconds != null) return row.stats_seconds
    return row.road_ids.reduce(
      (s, id) => s + (roadDurationSeconds(roads.find((r) => r.id === id) as RoadRecord) ?? 0),
      0,
    )
  }, [row.stats_seconds, row.road_ids, roads])
  return (
    <>
      {miles} mi · {formatDuration(seconds) ?? '—'}
    </>
  )
}

// Title + meta line shared by the in-grid chip and the floating drag clone.
function ChipMeta({
  row,
  roads,
  computing,
}: {
  row: SavedRouteRow
  roads: RoadRecord[]
  computing: boolean
}) {
  const names = row.road_ids
    .map((id) => roads.find((r) => r.id === id)?.name)
    .filter(Boolean) as string[]
  return (
    <>
      <div className="flex items-center gap-1.5 text-xs text-white/85">
        <RouteIcon size={12} className="text-amber-400 shrink-0" />
        <span className="truncate flex-1">
          {row.name ??
            `${names[0] ?? 'Saved route'}${names.length > 1 ? ` +${names.length - 1}` : ''}`}
        </span>
      </div>
      <div className="mt-0.5 text-[10px] text-white/65">
        {names.length} road{names.length === 1 ? '' : 's'}
        {' · '}
        {computing ? <span className="italic">updating…</span> : <SavedRouteStats row={row} roads={roads} />}
        {' · '}
        {new Date(row.saved_at).toLocaleDateString(undefined, {
          month: 'short',
          day: 'numeric',
        })}
      </div>
    </>
  )
}

const HOLD_MS = 500

export default function SavedRoutesPanel() {
  const { session, ready } = useAuth()
  const signedIn = ready && !!session
  const openAuthModal = useAuthUi((s) => s.openAuthModal)
  const routesSeq = useAuthUi((s) => s.routesSeq)
  const roads = useRoadStore((s) => s.roads)
  const addToRoute = useRoadStore((s) => s.addToRoute)
  const clearRoute = useRoadStore((s) => s.clearRoute)
  const focusRoute = useRoadStore((s) => s.focusRoute)
  const setRouteName = useRoadStore((s) => s.setRouteName)
  const setTrim = useRoadStore((s) => s.setTrim)
  const routeIdsForTrims = useRoadStore((s) => s.routeIds)
  const dismissOverlays = useRoadStore((s) => s.dismissOverlays)
  const mapClickSeq = useRoadStore((s) => s.mapClickSeq)
  const dismissOverlaysSeq = useRoadStore((s) => s.dismissOverlaysSeq)

  const [open, setOpen] = useState(false)
  const [saved, setSaved] = useState<SavedRouteRow[] | null>(null)
  const [loadingSaved, setLoadingSaved] = useState(false)
  const savedRef = useRef<SavedRouteRow[] | null>(null)
  savedRef.current = saved

  // Latest open state without re-running the routesSeq effect below.
  const openRef = useRef(open)
  openRef.current = open

  // Whether a modal sits above the panel (rename popup / remove dialog).
  // Declared after its state below; refs are assigned during render.
  const modalOpenRef = useRef(false)

  // Mobile top-sheet drag: the footer ("Your Routes" + drag bar) is the
  // handle; drag DOWN grows the sheet, drag UP shrinks it. Desktop mirrors
  // the road panel: the panel auto-sizes and is not draggable.
  const [sheetH, setSheetH] = useState<number | null>(null)
  const asideRef = useRef<HTMLElement | null>(null)
  const dragRef = useRef<{ startY: number; startH: number } | null>(null)
  const dragRafRef = useRef<number | null>(null)
  const pendingHRef = useRef<number | null>(null)
  // During the drag the height is written straight to the DOM — zero React
  // re-renders (the map keeps its headroom); state catches up once at the end.
  const applyDragHeight = (h: number) => {
    const el = asideRef.current
    if (el) {
      el.style.height = `${h}px`
      el.style.maxHeight = `${h}px`
    }
  }

  const onDragStart = (e: React.PointerEvent) => {
    if (window.matchMedia('(min-width: 768px)').matches) return
    if (e.pointerType === 'mouse' && e.button !== 0) return
    const current =
      sheetH ??
      asideRef.current?.offsetHeight ??
      Math.round(window.innerHeight * 0.35)
    dragRef.current = { startY: e.clientY, startH: current }
    try {
      ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
    } catch {
      /* no capture available */
    }
  }
  const onDragMove = (e: React.PointerEvent) => {
    if (!dragRef.current) return
    const dy = e.clientY - dragRef.current.startY
    const h = Math.min(
      Math.max(dragRef.current.startH + dy, 120),
      Math.max(window.innerHeight - 120, 200),
    )
    pendingHRef.current = h
    if (dragRafRef.current == null) {
      dragRafRef.current = requestAnimationFrame(() => {
        dragRafRef.current = null
        if (pendingHRef.current != null) applyDragHeight(pendingHRef.current)
      })
    }
  }
  const onDragEnd = () => {
    if (dragRafRef.current != null) {
      cancelAnimationFrame(dragRafRef.current)
      dragRafRef.current = null
    }
    const h = pendingHRef.current
    pendingHRef.current = null
    dragRef.current = null
    // Tap with no movement: fall back to the current DOM height so a tap on
    // an already-collapsed sheet still closes it.
    const finalH = h ?? asideRef.current?.offsetHeight ?? 0
    if (h != null) {
      applyDragHeight(h)
      setSheetH(h)
    }
    if (finalH < 150) {
      // Collapsed shut — forget the dragged height so the panel reopens at
      // its default size instead of a near-collapsed sliver.
      setSheetH(null)
      closePanel()
    }
  }

  // No reset-on-close: the last dragged height stays in `sheetH` for the
  // whole browsing session, so the mobile sheet reopens at that height.

  function sortRoutes(rows: SavedRouteRow[]): SavedRouteRow[] {
    // Drag placement first (stable), saved-date as tie-breaker for rows that
    // share the default sort_index.
    return [...rows]
      .map((r, i) => ({ r, i }))
      .sort((a, b) =>
        a.r.sort_index !== b.r.sort_index
          ? a.r.sort_index - b.r.sort_index
          : b.r.saved_at.localeCompare(a.r.saved_at) || a.i - b.i,
      )
      .map(({ r }) => r)
  }

  async function refresh() {
    // Pre-loaded list means the panel already knows its size — skip the
    // "Loading…" flash and just update in place.
    if (saved == null) setLoadingSaved(true)
    const rows = await fetchSavedRoutes()
    setSaved(sortRoutes(rows))
    setLoadingSaved(false)
  }

  // Pre-load the saved list as soon as the user is signed in so the panel
  // opens at its final size instead of resizing right after appearing.
  const preloadUidRef = useRef<string | null>(null)
  useEffect(() => {
    const uid = session?.user.id
    if (!ready || !signedIn || !uid) return
    if (preloadUidRef.current === uid) return
    preloadUidRef.current = uid
    void refresh()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, signedIn, session?.user.id])

  function closePanel() {
    // Never rip the panel out from under an active drag — finish the drag
    // first (drops the chip in its current slot), then close.
    if (dragIdRef.current != null || holdRef.current != null) {
      cancelHold()
      endDrag()
    }
    setOpen(false)
  }

  // "Your Routes" buttons (top bar / mobile search row): toggle the panel —
  // open it and load the saved list, or close it if it's already showing.
  // Signed-out users get the sign-in dialog.
  useEffect(() => {
    if (routesSeq === 0) return
    if (!ready) return
    if (!signedIn) {
      openAuthModal('panel')
      return
    }
    if (openRef.current) {
      setOpen(false)
      return
    }
    dismissOverlays()
    setOpen(true)
    void refresh()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routesSeq, ready])

  // Signing out while the panel is open closes it — the list is account-bound.
  useEffect(() => {
    if (ready && !signedIn) setOpen(false)
  }, [ready, signedIn])

  // Only one floating panel at a time: close whenever any other panel opens
  // (they all bump dismissOverlaysSeq) or a road/route gets selected.
  const selectedId = useRoadStore((s) => s.selectedId)
  useEffect(() => {
    if (selectedId) setOpen(false)
  }, [selectedId])
  useEffect(() => {
    if (dismissOverlaysSeq > 0 && openRef.current) setOpen(false)
  }, [dismissOverlaysSeq])
  // Road selection changes bump dismissOverlaysSeq only via dismissOverlays();
  // selectRoad(id) doesn't, so watch it directly (already declared above).

  // Catch-all: any pointer/click outside the panel (top bar, search input,
  // filters, find-a-ride, route tray…) closes it. Panning/zooming the map
  // doesn't emit click/pointerdown-without-move, so those don't close it.
  useEffect(() => {
    if (!open) return
    const isInside = (t: EventTarget | null) =>
      t instanceof Node &&
      (asideRef.current?.contains(t) ||
        // The floating reorder clone is also part of the panel.
        !!t.parentElement?.closest?.('[data-drag-clone]') ||
        // "Your Routes" toggle buttons handle their own open/close.
        !!(t instanceof Element && t.closest('[data-routes-toggle]')))
    const onOutside = (e: Event) => {
      if (isInside(e.target)) return
      if (dragIdRef.current != null || holdRef.current != null) return
      // A modal (mobile rename popup / remove confirmation) is open above the
      // panel: taps land outside the panel element by definition, but they
      // belong to the modal — never close the panel underneath it.
      if (modalOpenRef.current) return
      closePanel()
    }
    document.addEventListener('pointerdown', onOutside, true)
    document.addEventListener('click', onOutside, true)
    return () => {
      document.removeEventListener('pointerdown', onOutside, true)
      document.removeEventListener('click', onOutside, true)
    }
  }, [open])

  // While the panel is open, lock the page behind it: a dimmed backdrop
  // swallows touches on the map (so drag-scrolling there can't move the
  // whole app screen), and body scroll is suspended. Drag scrolling INSIDE
  // the panel still scrolls the route chips normally.
  const backdropRef = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    if (!open) return
    const el = backdropRef.current
    const stop = (e: TouchEvent) => e.preventDefault()
    el?.addEventListener('touchmove', stop, { passive: false })
    const prevOverflow = document.body.style.overflow
    const prevOverscroll = document.body.style.overscrollBehavior
    document.body.style.overflow = 'hidden'
    document.body.style.overscrollBehavior = 'none'
    return () => {
      el?.removeEventListener('touchmove', stop)
      document.body.style.overflow = prevOverflow
      document.body.style.overscrollBehavior = prevOverscroll
    }
  }, [open])

  // Tapping the map closes the panel — but never mid-drag: with the panel
  // gone the swap logic would have no list to work against. The backdrop
  // already covers the map while the panel is open, so this mostly guards
  // programmatic close paths.
  useEffect(() => {
    if (!open) return
    if (dragIdRef.current != null || holdRef.current != null) return
    if (openRef.current) setOpen(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapClickSeq])

  // Saving/unsaving a route elsewhere (e.g. the route tray) refreshes the
  // list live while the panel is open.
  useEffect(() => {
    return onSavedRoutesChanged(() => {
      if (!open) {
        setSaved(null)
        return
      }
      void refresh()
    })
  }, [open, session?.user.id])

  async function handleDelete(id: string) {
    setSaved((prev) => prev?.filter((r) => r.id !== id) ?? prev)
    await deleteSavedRoute(id)
  }

  // Removal asks for confirmation first — the dialog sits above the panel
  // (z-[60]) and its backdrop swallows the outside-click close handlers.
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null)

  // ── Shared route stats: recompute on selection when older than 30 min ────
  const [computingIds, setComputingIds] = useState<Set<string>>(new Set())

  async function recomputeStats(row: SavedRouteRow) {
    if (routeStatsFresh(row)) return
    setComputingIds((prev) => new Set(prev).add(row.id))
    try {
      const routeRoads = row.road_ids
        .map((id) => roads.find((r) => r.id === id))
        .filter((r): r is RoadRecord => !!r)
      const pts = await buildRouteWaypoints(routeRoads)
      if (pts.length >= 2) {
        const { data, error } = await supabase.functions.invoke('route-summary', {
          body: { waypoints: pts },
        })
        if (!error && data && typeof data.distance_miles === 'number') {
          const miles = data.distance_miles as number
          const seconds =
            typeof data.duration_seconds === 'number' ? (data.duration_seconds as number) : null
          // Shared table — every user with this route sees the new totals.
          await saveRouteStats(row.road_ids, miles, seconds)
          setSaved((prev) =>
            prev?.map((r) =>
              r.id === row.id
                ? { ...r, stats_miles: miles, stats_seconds: seconds, stats_computed_at: new Date().toISOString() }
                : r,
            ) ?? prev,
          )
        }
      }
    } catch {
      /* routing unavailable — fallback totals stay visible */
    }
    setComputingIds((prev) => {
      const next = new Set(prev)
      next.delete(row.id)
      return next
    })
  }

  // Per-route rename: desktop flips the chip's title into an inline input
  // (Enter commits, Escape cancels). Mobile opens a dedicated popup instead —
  // the inline input inside the sheet gets obscured and crowded out there.
  const [renamingId, setRenamingId] = useState<string | null>(null)
  const [renameValue, setRenameValue] = useState('')
  const [renameBusy, setRenameBusy] = useState(false)
  const [renameError, setRenameError] = useState<string | null>(null)
  const [renamePopup, setRenamePopup] = useState(false)
  modalOpenRef.current = renamePopup || confirmDeleteId != null

  function startRename(row: SavedRouteRow) {
    setRenamingId(row.id)
    setRenameValue(row.name ?? '')
    setRenameError(null)
    setRenamePopup(!window.matchMedia('(min-width: 768px)').matches)
  }

  function cancelRename() {
    setRenamingId(null)
    setRenamePopup(false)
    setRenameError(null)
  }

  const renamingRow = saved?.find((r) => r.id === renamingId) ?? null

  async function commitRename(row: SavedRouteRow) {
    if (!renameBusy) {
      setRenameBusy(true)
      const trimmed = renameValue.trim()
      if (trimmed && trimmed !== (row.name ?? '') && session?.user.id) {
        const { error } = await renameSavedRoute(row.id, session.user.id, trimmed)
        if (error) {
          setRenameError(error)
          setRenameBusy(false)
          return
        }
        setSaved((prev) => prev?.map((r) => (r.id === row.id ? { ...r, name: trimmed } : r)) ?? prev)
      }
      setRenameBusy(false)
    }
    setRenamingId(null)
    setRenamePopup(false)
  }

  function loadRoute(row: SavedRouteRow) {
    // Replace whatever route is currently loaded first — loading a saved
    // route must never merge into the previous selection.
    clearRoute()
    row.road_ids.forEach((id) => addToRoute(id))
    // Carry the saved name into the tray header (addToRoute cleared it).
    if (row.name) setRouteName(row.name)
    // Restore the saved trims. Keys are per road OCCURRENCE (1st pass = plain
    // id, later passes = "id#2"…), so numbered keys are accepted as long as
    // the base road appears in the route.
    if (row.trims) {
      for (const [key, trim] of Object.entries(row.trims)) {
        const baseId = key.split('#')[0]
        if (key && trim && (trim.head || trim.tail) && routeIdsForTrims.includes(baseId)) setTrim(key, trim)
      }
    }
    closePanel()
    // Frame the whole route on the map — no individual road gets selected, so
    // no road detail panel opens and nothing flies to the first road.
    focusRoute()
    // Freshen the shared totals when they're older than 30 minutes.
    void recomputeStats(row)
  }

  // ── Drag-to-reorder ─────────────────────────────────────────────────────
  // Press-and-HOLD a chip (0.5s, without scrolling away) to engage reordering.
  // The chip then pops out as a floating clone that follows the pointer while
  // the rest of the grid animates smoothly around the empty slot. A swap
  // happens only when the pointer crosses another chip's centre, in the
  // direction the pointer is travelling.
  const [dragId, setDragId] = useState<string | null>(null)
  const [dragPos, setDragPos] = useState<{ x: number; y: number } | null>(null)
  const [dragSize, setDragSize] = useState<{ w: number; h: number } | null>(null)
  const dragIdRef = useRef<string | null>(null)
  dragIdRef.current = dragId
  const lastPointer = useRef<{ x: number; y: number } | null>(null)
  // Chip most recently swapped with — locked out as a swap target until the
  // pointer leaves its box (see onGlobalPointerMove).
  const swapTargetRef = useRef<string | null>(null)
  const holdRef = useRef<{
    id: string
    startX: number
    startY: number
    pointerId: number
    el: HTMLElement
    timer: ReturnType<typeof setTimeout> | null
  } | null>(null)
  const suppressClickRef = useRef(false)
  const listRef = useRef<HTMLDivElement | null>(null)

  // While a chip drag is active, stop the browser from scrolling the list out
  // from under the drag (touch devices).
  function preventTouchMove(e: TouchEvent) {
    if (dragIdRef.current != null) e.preventDefault()
  }

  function cancelHold() {
    if (holdRef.current?.timer) clearTimeout(holdRef.current.timer)
    holdRef.current = null
  }

  function moveChipToChip(id: string, otherId: string) {
    setSaved((prev) => {
      if (!prev) return prev
      const from = prev.findIndex((r) => r.id === id)
      const to = prev.findIndex((r) => r.id === otherId)
      if (from === -1 || to === -1 || from === to) return prev
      const next = [...prev]
      const [item] = next.splice(from, 1)
      next.splice(to, 0, item)
      return next
    })
    // Lock the chip we just swapped with: it now springs to its new slot, and
    // while gliding its box passes under the pointer. Without the lock the
    // swap logic sees its centre "crossed" again and swaps back — the two
    // chips oscillate until the spring damps out. Unlock on pointer exit.
    swapTargetRef.current = otherId
  }

  // The other chip (if any) whose centre the pointer just crossed.
  function swapTarget(
    px: number,
    py: number,
    id: string,
    prev: { x: number; y: number } | null,
  ): string | null {
    const list = listRef.current
    if (!list) return null
    const chips = Array.from(list.children).filter(
      (el): el is HTMLElement =>
        el instanceof HTMLElement && !!el.dataset.chipId && el.dataset.chipId !== id,
    )
    for (const el of chips) {
      const r = el.getBoundingClientRect()
      if (px < r.left || px > r.right || py < r.top || py > r.bottom) continue
      if (el.dataset.chipId === swapTargetRef.current) return null
      const cx = r.left + r.width / 2
      const cy = r.top + r.height / 2
      const dx = px - cx
      const dy = py - cy
      if (prev) {
        // A swap requires REAL pointer movement in the swap direction. With
        // touch the finger is often parked while chips glide beneath it; a
        // stationary pointer (px == prev.x) used to count as "moving down",
        // so every chip gliding under the parked finger fired a swap and the
        // order oscillated. Requiring movement >1px parks the finger safely.
        const moveX = px - prev.x
        const moveY = py - prev.y
        // Dominant movement axis decides which centre line counts.
        const movingX = Math.abs(moveX) >= Math.abs(moveY)
        if (movingX) {
          if ((dx > 0 && moveX > 1) || (dx < 0 && moveX < -1)) return el.dataset.chipId!
        } else {
          if ((dy > 0 && moveY > 1) || (dy < 0 && moveY < -1)) return el.dataset.chipId!
        }
      } else if (Math.abs(dx) > r.width / 4 || Math.abs(dy) > r.height / 4) {
        return el.dataset.chipId!
      }
      return null
    }
    return null
  }

  function onChipPointerDown(e: React.PointerEvent, id: string) {
    if (e.pointerType === 'mouse' && e.button !== 0) return
    cancelHold()
    const el = e.target as HTMLElement
    holdRef.current = {
      id,
      startX: e.clientX,
      startY: e.clientY,
      pointerId: e.pointerId,
      el,
      timer: setTimeout(() => {
        const h = holdRef.current
        if (!h) return
        const rect = h.el.closest('[data-chip-id]')?.getBoundingClientRect()
        if (rect) setDragSize({ w: rect.width, h: rect.height })
        setDragPos({ x: h.startX, y: h.startY })
        lastPointer.current = { x: h.startX, y: h.startY }
        setDragId(h.id)
        document.addEventListener('touchmove', preventTouchMove, { passive: false })
      }, HOLD_MS),
    }
    // Track the rest of the gesture at WINDOW level. The pointer can leave
    // the chip, the panel, the map — even the browser window — and the drag
    // must still end cleanly wherever the release happens. Chip-level
    // handlers die if the chip or the panel unmounts mid-drag (map tap,
    // sign-out, close), which previously stranded the drag forever.
    window.addEventListener('pointermove', onGlobalPointerMove)
    window.addEventListener('pointerup', onGlobalPointerUp)
    window.addEventListener('pointercancel', onGlobalPointerUp)
    window.addEventListener('blur', onGlobalPointerUp)
    window.addEventListener('keydown', onGlobalKeyDown)
  }

  function onGlobalPointerMove(e: PointerEvent) {
    const h = holdRef.current
    if (dragIdRef.current == null) {
      // Moved before the hold fired — it's a scroll/click, not a drag.
      if (h && Math.hypot(e.clientX - h.startX, e.clientY - h.startY) > 10) cancelHold()
      return
    }
    const prev = lastPointer.current
    lastPointer.current = { x: e.clientX, y: e.clientY }
    setDragPos({ x: e.clientX, y: e.clientY })
    const otherId = swapTarget(e.clientX, e.clientY, dragIdRef.current, prev)
    if (otherId) {
      moveChipToChip(dragIdRef.current, otherId)
    } else if (swapTargetRef.current) {
      // Pointer left the locked chip's box (or is over empty space) — allow
      // swapping with it again on the next entry.
      const list = listRef.current
      const stillOver = list
        ? Array.from(list.children).some(
            (el) =>
              el instanceof HTMLElement &&
              el.dataset.chipId === swapTargetRef.current &&
              (() => {
                const r = el.getBoundingClientRect()
                return e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom
              })(),
          )
        : false
      if (!stillOver) swapTargetRef.current = null
    }
  }

  function onGlobalKeyDown(e: KeyboardEvent) {
    // Escape drops the chip where it currently sits.
    if (e.key === 'Escape') endDrag()
  }

  // Drop point in the panel's empty space (the panel is taller than the list)
  // counts as "after the last chip": move the dragged chip to the end so a
  // release below the list never silently discards the reorder. Returns the
  // (possibly reordered) rows synchronously so the caller can persist them.
  function applyEmptySpaceDrop(cur: SavedRouteRow[]): SavedRouteRow[] {
    const id = dragIdRef.current
    const pos = lastPointer.current
    const list = listRef.current
    if (!id || !pos || !list) return cur
    const container = list.parentElement
    if (!container) return cur
    const cr = container.getBoundingClientRect()
    // Released outside the panel's list area (map, backdrop, footer) — keep
    // the order as it stands.
    if (pos.x < cr.left || pos.x > cr.right || pos.y < cr.top || pos.y > cr.bottom) return cur
    // Genuine empty space only: below the bottom of the last chip (mobile
    // list taller than its content, desktop panel grown for scrolling) or,
    // on the desktop grid's last row, to the right of the last chip. The
    // tiny gaps BETWEEN chips don't count — a release passing through a gap
    // mid-drag must not yank the chip to the end.
    const chips = Array.from(list.children).filter(
      (el): el is HTMLElement =>
        el instanceof HTMLElement && !!el.dataset.chipId && el.dataset.chipId !== id,
    )
    if (chips.length === 0) return cur
    let last: HTMLElement = chips[0]
    for (const el of chips) {
      const a = el.getBoundingClientRect()
      const b = last.getBoundingClientRect()
      if (a.top > b.top || (a.top === b.top && a.left > b.left)) last = el
    }
    const lr = last.getBoundingClientRect()
    const inEmptySpace =
      pos.y > lr.bottom || (pos.y >= lr.top && pos.x > lr.right)
    if (!inEmptySpace) return cur
    const from = cur.findIndex((r) => r.id === id)
    if (from === -1 || from === cur.length - 1) return cur
    const next = [...cur]
    const [item] = next.splice(from, 1)
    next.push(item)
    return next
  }

  function endDrag() {
    document.removeEventListener('touchmove', preventTouchMove)
    window.removeEventListener('pointermove', onGlobalPointerMove)
    window.removeEventListener('pointerup', onGlobalPointerUp)
    window.removeEventListener('pointercancel', onGlobalPointerUp)
    window.removeEventListener('blur', onGlobalPointerUp)
    window.removeEventListener('keydown', onGlobalKeyDown)
    const wasDragging = dragIdRef.current != null
    if (wasDragging) {
      suppressClickRef.current = true
      setTimeout(() => {
        suppressClickRef.current = false
      }, 0)
      // Persist the final order (the drag produced it live; a drop in the
      // empty space below the list snaps the chip to the end first).
      let cur = savedRef.current
      if (cur) {
        cur = applyEmptySpaceDrop(cur)
        const next = cur.map((r, i) => ({ ...r, sort_index: i }))
        setSaved(next)
        void reorderSavedRoutes(next.map((r) => r.id))
      }
    }
    cancelHold()
    setDragId(null)
    setDragPos(null)
    setDragSize(null)
    lastPointer.current = null
    swapTargetRef.current = null
  }

  function onGlobalPointerUp() {
    endDrag()
  }

  // Safety net: if this component unmounts mid-gesture, release every global
  // listener so a drag can never get stuck.
  useEffect(() => {
    return () => {
      document.removeEventListener('touchmove', preventTouchMove)
      window.removeEventListener('pointermove', onGlobalPointerMove)
      window.removeEventListener('pointerup', onGlobalPointerUp)
      window.removeEventListener('pointercancel', onGlobalPointerUp)
      window.removeEventListener('blur', onGlobalPointerUp)
      window.removeEventListener('keydown', onGlobalKeyDown)
      cancelHold()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const dragRow = saved?.find((r) => r.id === dragId) ?? null

  return (
    <>
      {/* Scroll/interaction shield behind the open panel */}
      {open && (
        <div
          ref={backdropRef}
          onPointerDown={closePanel}
          className="fixed inset-0 z-40 bg-black/30"
        />
      )}

      <AnimatePresence>
        {open && (
          <motion.aside
            ref={asideRef}
            initial={{ opacity: 0, y: -40 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -40 }}
            transition={{ type: 'spring', damping: 28, stiffness: 300 }}
            style={sheetH != null ? { height: sheetH, maxHeight: sheetH } : undefined}
            className="fixed md:absolute z-50 bg-[#0C0C0E]/95 md:backdrop-blur border-2 border-white
              rounded-xl overflow-hidden flex flex-col max-h-[33.5dvh]
              inset-x-2 top-[7.5rem]
              md:inset-x-3 md:top-[4.5rem] md:w-auto md:max-h-[33.5vh] md:h-auto"
          >
            <div className="overflow-y-auto min-h-0 flex-1 overscroll-contain px-3 pt-3 pb-1">
              {loadingSaved ? (
                <div className="py-6 text-center text-xs text-white/40">Loading…</div>
              ) : saved && saved.length > 0 ? (
                // Mobile: one chip per row. Desktop: fixed 5-per-row grid — the
                // chips stretch equally to fill the panel edge to edge.
                <div ref={listRef} className="grid grid-cols-1 md:grid-cols-5 gap-2">
                  {saved.map((row) => {
                    const dragging = dragId === row.id
                    return (
                      <motion.div
                        key={row.id}
                        layout
                        transition={{ type: 'spring', stiffness: 500, damping: 40 }}
                        data-chip-id={row.id}
                        onPointerDown={(e) => onChipPointerDown(e, row.id)}
                        className={`w-full min-w-0 rounded-lg bg-white/[0.04] border px-2.5 py-2
                          transition-colors flex flex-col select-none touch-pan-y
                          ${
                            dragging
                              ? 'invisible'
                              : renamingId === row.id
                                ? 'border-amber-400/60'
                                : 'border-white/10 hover:border-amber-400/40'
                          }`}
                      >
                        {renamingId === row.id && !renamePopup ? (
                          <div className="space-y-1">
                            <input
                              autoFocus
                              value={renameValue}
                              onChange={(e) => setRenameValue(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') void commitRename(row)
                                if (e.key === 'Escape') setRenamingId(null)
                              }}
                              placeholder="Route name"
                              maxLength={60}
                              className="w-full rounded-md bg-black/50 border border-amber-400/60 px-2 py-1.5 text-xs text-white placeholder:text-white/30 focus:outline-none"
                            />
                            {renameError && (
                              <div className="text-[10px] text-red-400">{renameError}</div>
                            )}
                          </div>
                        ) : renamingId === row.id && renamePopup ? (
                          <div className="text-xs text-amber-400 italic">Renaming…</div>
                        ) : (
                          <button
                            onClick={() => {
                              if (suppressClickRef.current) return
                              loadRoute(row)
                            }}
                            className={`w-full text-left ${dragging ? '' : 'cursor-grab active:cursor-grabbing'}`}
                            title="Hold to reorder · tap to load this route"
                          >
                            <ChipMeta row={row} roads={roads} computing={computingIds.has(row.id)} />
                          </button>
                        )}
                        <div className="mt-1 flex items-center justify-between">
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              setConfirmDeleteId(row.id)
                            }}
                            className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-white/60 hover:text-red-400"
                          >
                            <Trash2 size={10} /> Remove
                          </button>
                          {renamingId === row.id ? (
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                void commitRename(row)
                              }}
                              disabled={renameBusy}
                              className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-amber-400 hover:text-amber-300 disabled:opacity-60"
                            >
                              <Check size={10} /> Save
                            </button>
                          ) : (
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                startRename(row)
                              }}
                              className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-white/60 hover:text-amber-300"
                            >
                              <Pencil size={10} /> Rename
                            </button>
                          )}
                        </div>
                      </motion.div>
                    )
                  })}
                </div>
              ) : (
                <p className="text-xs text-white/45 py-4 text-center px-4">
                  No saved routes yet — build one with “Add to route”, then hit Save in the route
                  tray.
                </p>
              )}
            </div>

            {/* Footer: title + drag bar. On mobile this whole block is the drag
                handle (drag down = grow, up = shrink/collapse). */}
            <div
              onPointerDown={onDragStart}
              onPointerMove={onDragMove}
              onPointerUp={onDragEnd}
              onPointerCancel={onDragEnd}
              className="shrink-0 z-10 bg-[#0C0C0E] select-none touch-none md:cursor-default"
            >
              <div className="flex justify-center pt-2 pb-1.5 md:hidden">
                <div className="w-10 h-1 rounded-full bg-white/25" />
              </div>
              <div className="relative px-4 pb-3 pt-1 flex items-center justify-center gap-3">
                <div className="text-sm font-mono uppercase tracking-widest text-amber-400">
                  Your Routes
                </div>
                <button
                  onClick={closePanel}
                  aria-label="Close"
                  className="absolute right-3 rounded-lg p-1 text-white/40 hover:bg-white/10 hover:text-white"
                >
                  <X size={16} />
                </button>
              </div>
            </div>
          </motion.aside>
        )}
      </AnimatePresence>

      {/* Mobile rename popup — centered and full-width so the keyboard can't
          push it around and the input is always clearly visible. Sits above
          the panel and its backdrop. */}
      {renamePopup && renamingId && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 px-5"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={cancelRename}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-xs rounded-xl border-2 border-white bg-[#0C0C0E]/95 backdrop-blur p-4 shadow-2xl shadow-black/60"
          >
            <p className="text-xs uppercase tracking-widest text-amber-400 text-center">
              Rename Route
            </p>
            <input
              autoFocus
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void commitRename(renamingRow)
                if (e.key === 'Escape') cancelRename()
              }}
              placeholder="Route name"
              maxLength={60}
              className="mt-3 w-full rounded-lg bg-black/50 border-2 border-amber-400/60 px-3 py-2.5 text-base text-white placeholder:text-white/30 focus:outline-none"
            />
            {renameError && (
              <div className="mt-2 text-[11px] text-red-400 text-center">{renameError}</div>
            )}
            <div className="mt-4 flex gap-3">
              <button
                onClick={() => void commitRename(renamingRow)}
                disabled={renameBusy || !renamingRow}
                className="flex-1 rounded-lg bg-amber-400 px-3 py-2 text-sm font-medium text-black hover:bg-amber-300 disabled:opacity-60"
              >
                OK
              </button>
              <button
                onClick={cancelRename}
                className="flex-1 rounded-lg border-2 border-amber-400 bg-black px-3 py-2 text-sm font-medium text-amber-400 hover:bg-white/5"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Remove confirmation — sits above the panel and its backdrop. */}
      {confirmDeleteId && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 px-6"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={() => setConfirmDeleteId(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-xs rounded-xl border-2 border-white bg-[#0C0C0E]/95 backdrop-blur p-4 shadow-2xl shadow-black/60"
          >
            <p className="text-sm text-white/85 text-center">
              You are about to remove this route from Your Routes. Are you sure?
            </p>
            <div className="mt-4 flex gap-3">
              <button
                onClick={() => {
                  const id = confirmDeleteId
                  setConfirmDeleteId(null)
                  if (id) void handleDelete(id)
                }}
                className="flex-1 rounded-lg bg-amber-400 px-3 py-2 text-sm font-medium text-black hover:bg-amber-300"
              >
                Yes
              </button>
              <button
                onClick={() => setConfirmDeleteId(null)}
                className="flex-1 rounded-lg border-2 border-amber-400 bg-black px-3 py-2 text-sm font-medium text-amber-400 hover:bg-white/5"
              >
                No
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Floating drag clone: slightly larger, popped in front, follows the
          pointer with its centre snapped to the finger/mouse position. */}
      {dragRow && dragPos && dragSize && (
        <div
          data-drag-clone
          className="fixed z-[60] pointer-events-none"
          style={{
            left: dragPos.x,
            top: dragPos.y,
            width: dragSize.w,
            transform: 'translate(-50%, -50%) scale(1.06) rotate(-1.5deg)',
          }}
        >
          <div
            className="rounded-lg bg-[#17171B] border-2 border-amber-400 px-2.5 py-2 flex flex-col
              shadow-2xl shadow-black/60"
            style={{ minHeight: dragSize.h }}
          >
            <ChipMeta row={dragRow} roads={roads} computing={computingIds.has(dragRow.id)} />
          </div>
        </div>
      )}
    </>
  )
}
